import { useMatch } from 'react-router';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../store/hooks';
import {
  selectAccountPermissionStatus,
  selectHasAccountPermission,
  selectHasActiveLocationPermission,
  selectLocationPermissionStatus,
  type PermissionStatus
} from '../store/permissions/permissionsSlice';
import type { RootState } from '../store';
import {
  PERMISSION_SCOPES,
  getPermissionScope,
  type Permission
} from '../constants/permissions';
import { LOCATION_ROUTE_PATTERN } from '../routes/routeConstants';

/**
 * Whether the signed-in user holds `permission`, asking the question the
 * permission's own scope calls for — an account permission tenant-wide, a
 * location permission at the location currently loaded.
 *
 * That the scope decides means a gate cannot check an account permission
 * against a location, or a location permission without one.
 */
export function useHasPermission(permission: Permission): boolean {
  return useAppSelector((state) => hasPermission(state, permission));
}

/**
 * The permissions among `permissions` the signed-in user holds, each resolved by
 * its own scope like `useHasPermission`. For menus and redirects that filter a
 * list of pages by permission, where one hook call per entry is not possible.
 *
 * Compared shallowly so a store update that changes nothing here does not
 * re-render the caller.
 */
export function useGrantedPermissions(permissions: readonly Permission[]): Permission[] {
  return useAppSelector(
    (state) => permissions.filter((permission) => hasPermission(state, permission)),
    shallowEqual
  );
}

const hasPermission = (state: RootState, permission: Permission): boolean =>
  getPermissionScope(permission) === PERMISSION_SCOPES.account
    ? selectHasAccountPermission(permission)(state)
    : selectHasActiveLocationPermission(permission)(state);

/**
 * The same question, but keeping "not known yet" apart from "no". For route
 * guards, which must not redirect a user who is in fact allowed in just because
 * their permissions are still in flight.
 *
 * Location-scoped permissions are resolved against the route's own `:locationId`
 * rather than whatever the store last loaded, so a guard cannot admit someone on
 * the strength of another location's permissions.
 */
export function usePermissionStatus(permission: Permission): PermissionStatus {
  const locationId = useMatch(LOCATION_ROUTE_PATTERN)?.params.locationId ?? null;
  const isAccountScoped = getPermissionScope(permission) === PERMISSION_SCOPES.account;
  const accountStatus = useAppSelector(selectAccountPermissionStatus(permission));
  const locationStatus = useAppSelector(
    selectLocationPermissionStatus(permission, locationId)
  );

  return isAccountScoped ? accountStatus : locationStatus;
}
